import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ExperienceSession, SessionState } from '@aeonia/gaia-client';
import { createSession } from './session';

export interface UseSession {
  state: SessionState;
  start: () => void;
  sendTalk: (text: string) => void;
  retry: () => void;
  /** Reads the session's live phase, not the last one React rendered with. */
  canStart: () => boolean;
  session: ExperienceSession;
}

/**
 * Holds one ExperienceSession for the life of the page and reports its state
 * to React. The session is built once, never rebuilt on a re-render, and is
 * disposed when the component goes away.
 */
export default function useSession(): UseSession {
  const sessionRef = useRef<ExperienceSession>(null);
  if (sessionRef.current === null) {
    sessionRef.current = createSession();
  }
  const session = sessionRef.current;

  const subscribe = useCallback(
    (onChange: () => void) => session.subscribe(() => onChange()),
    [session],
  );
  const state = useSyncExternalStore(
    subscribe,
    () => session.state,
    () => session.state,
  );

  // Dispose when the page drops the component. The dispose is deferred by one
  // task and cancelled if the component mounts again, because StrictMode in
  // development unmounts and remounts once on purpose and the session has to
  // survive that.
  const disposeTimer = useRef(0);
  useEffect(() => {
    clearTimeout(disposeTimer.current);
    return () => {
      disposeTimer.current = window.setTimeout(() => session.dispose(), 0);
    };
  }, [session]);

  const start = useCallback(() => {
    void session.start();
  }, [session]);
  const sendTalk = useCallback(
    (text: string) => {
      session.sendTalk(text);
    },
    [session],
  );
  const retry = useCallback(() => {
    void session.retry();
  }, [session]);
  // Reads session.state directly (not the `state` from useSyncExternalStore)
  // so that two Enters in the same event-loop turn cannot both see 'idle'.
  const canStart = useCallback(() => session.state.phase === 'idle', [session]);

  return { state, start, sendTalk, retry, canStart, session };
}
